import React from 'react';
import {
  Paper, Box, Stack, Typography, Button, useTheme, alpha, Tooltip,
} from '@mui/material';
import { EditOutlined as Edit, LockOutlined } from '@mui/icons-material';

/**
 * SectionCard — one editable settings section. Keeps the house style
 * consistent across every editor on the Settings tab: Paper with hairline
 * border, title + description header, optional Edit button that flips the
 * card into an edit mode with Save / Cancel in the footer.
 *
 * Children render the body in two modes:
 *   • read-only (editing=false) — pure display, no inputs
 *   • editing   (editing=true)  — inputs, local state owned by the child
 *
 * The parent passes `onSave()` which does the actual API call and should
 * throw on failure. SectionCard handles the loading state + exit-edit on
 * success. `onCancel` is the child's chance to reset local state.
 */

interface Props {
  title: string;
  description?: React.ReactNode;
  /** When false, the Edit button is hidden (read-only mode — e.g. closed cycles). */
  canEdit?: boolean;
  /** Why this section can't change right now (e.g. "Test marks are in for
   *  12 applicants…"). Shown under the description; hides the Edit button. */
  lockedReason?: string | null;
  editing: boolean;
  onEditToggle: (next: boolean) => void;
  onSave: () => Promise<void> | void;
  onCancel: () => void;
  saving?: boolean;
  /** Optional disable condition for the Save button (e.g. invalid form). */
  canSave?: boolean;
  /** Optional actions rendered in the header (e.g. "Add row" when editing). */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}

const SectionCard: React.FC<Props> = ({
  title,
  description,
  canEdit = true,
  lockedReason,
  editing,
  onEditToggle,
  onSave,
  onCancel,
  saving,
  canSave = true,
  headerActions,
  children,
}) => {
  const theme = useTheme();

  const handleSave = async () => {
    try {
      await onSave();
    } catch {
      // Caller surfaces via snackbar — leave edit mode open.
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${editing ? alpha(theme.palette.primary.main, 0.6) : theme.palette.divider}`,
        boxShadow: editing ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.08)}` : 'none',
        overflow: 'hidden',
        transition: 'border-color 160ms, box-shadow 160ms',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ px: 3, pt: 2.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{description}</Typography>
          )}
          {lockedReason && !editing && (
            <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1 }}>
              <LockOutlined sx={{ fontSize: 15, mt: '2px', color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>{lockedReason}</Typography>
            </Stack>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {editing && headerActions}
          {!editing && canEdit && !lockedReason && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Edit sx={{ fontSize: '16px !important' }} />}
              onClick={() => onEditToggle(true)}
              sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}
            >
              Edit
            </Button>
          )}
          {(!canEdit || lockedReason) && (
            <Tooltip title={lockedReason || 'Read-only for this cycle'}>
              <Typography variant="caption" color="text.disabled" sx={{ px: 1, py: 0.25, borderRadius: 5, border: 1, borderColor: 'divider' }}>
                Locked
              </Typography>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Box sx={{ px: 3, pt: 2, pb: 3 }}>{children}</Box>

      {editing && (
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ px: 3, py: 1.75, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.03) }}
        >
          <Button
            size="small"
            onClick={() => { onCancel(); onEditToggle(false); }}
            disabled={saving}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={saving || !canSave}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Stack>
      )}
    </Paper>
  );
};

export default SectionCard;
